'use client';

import React from 'react';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { WarningCircleIcon } from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { Modal } from '@tranquility-ai/theme-components/modal';
import { Icon, Inline, Stack, Text } from '@tranquility-ai/theme-components/primitives';
import '@tranquility-ai/theme-components/modal.css';

import './flag-csam-confirm-modal.css';

export type FlagCsamConfirmModalProps = {
  isOpen: boolean;
  fileLabel?: string;
  isPending?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * Irreversible confirm dialog for user-initiated CSAM flagging (TR-3168).
 * Candidate for promotion to theme-components once the flow is productionized.
 */
export function FlagCsamConfirmModal({
  isOpen,
  fileLabel,
  isPending = false,
  error,
  onCancel,
  onConfirm,
}: FlagCsamConfirmModalProps) {
  const t = useTimePilotT();
  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      onClose={onCancel}
      title={t('csam.flag.title')}
      size="sm"
      dismissOnEscape={!isPending}
      dismissOnBackdropClick={!isPending}
      footer={
        <Inline justify="between" align="center" className="tp-flag-csam-confirm-footer">
          <Button
            type="button"
            size="sm"
            style="text"
            color="light"
            background="inverse"
            onClick={onCancel}
            disabled={isPending}
          >
            {t('timeline.dialog.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            style="solid"
            color="danger"
            background="inverse"
            onClick={onConfirm}
            disabled={isPending}
            isLoading={isPending}
            loadingText={t('csam.flag.flagging')}
          >
            {t('csam.flag.confirm')}
          </Button>
        </Inline>
      }
    >
      <Stack gap="md" className="tp-flag-csam-confirm">
        <Stack gap="sm">
          <Icon icon={WarningCircleIcon} size="lg" weight="fill" />
          <Text variant="bodySmall">
            {fileLabel
              ? t('csam.flag.bodyNamed', { name: fileLabel })
              : t('csam.flag.body')}
          </Text>
          {error ? (
            <div className="tp-flag-csam-confirm-error" role="alert">
              <Text variant="bodySmall">{error}</Text>
            </div>
          ) : null}
        </Stack>
      </Stack>
    </Modal>
  );
}
